import { Base } from "@app/controllers/base";
import { TrackingChannel } from "@app/channels/store/tracking_channel";
import Swiper from "swiper";

// Connects to data-controller="store-page--tracking"
export default class extends Base {
  static values = { code: String }
  static targets = [
    "prevContainer",
    "prevShowOverlay",
    "prevHideBtn",
    "nextContainer",
    "nextShowOverlay",
    "nextHideBtn",
    "estimatedTime",
    "liveBanner", 
    "livePulse", 
    "liveDot", 
    "liveStatusTitle", 
    "liveStatusDesc", 
    "flightTime", 
    "flightAlt", 
    "flightDist",
  ]

  connect() {
    console.log("Tracking controller connected with code:", this.codeValue);
    this.subscriptions = {};
    this.subscribeChannel();
    this.resizeHandler = this.handleResize.bind(this);
    window.addEventListener("resize", this.resizeHandler);
    this.initTimelineSwiper();
    this.startTimers();
  }

  disconnect() {
    if (this.subscriptions.trackingChannel) {
      this.subscriptions.trackingChannel.disconnect();
      delete this.subscriptions.trackingChannel;
    }
    window.removeEventListener("resize", this.resizeHandler);
    this.destroyTimelineSwiper();
    this.stopTimers();
  }

  startTimers() {
    this.timers = [];
    console.log("Starting timers, targets found:", this.estimatedTimeTargets.length);
    this.estimatedTimeTargets.forEach((target) => {
      const estimatedAtStr = target.dataset.estimatedAt;
      const delayedText = target.dataset.delayedText;
      if (!estimatedAtStr || !delayedText) return;

      const estimatedAt = new Date(estimatedAtStr).getTime();
      const now = new Date().getTime();
      const timeToWait = estimatedAt - now;

      if (timeToWait > 0) {
        const timerId = setTimeout(() => {
          target.innerText = delayedText;
        }, timeToWait);
        this.timers.push(timerId);
      } else {
        target.innerText = delayedText;
      }
    });
  }

  stopTimers() {
    if (this.timers) {
      this.timers.forEach(timerId => clearTimeout(timerId));
    }
  }

  async subscribeChannel() {
    this.trackingChannel = new TrackingChannel({
      channel: "Store::TrackingChannel",
      params: {
        code: this.codeValue,
      },
      attributes: {
        trackingController: this,
      }
    });

    this.subscriptions = {
      trackingChannel: await this.trackingChannel.subscribe(),
    }
  }

  handleMessage(data) {
    if (data.type === "telemetry") {
      this.showLiveBanner();
      this.updateTelemetry(data);
    } else if (data.type === "mission_paused") {
      this.showPausedState();
    } else if (data.type === "mission_stopped" || data.type === "mission_completed") {
      this.hideLiveBanner();
      if (window.Turbo) {
        Turbo.visit(window.location.href, { action: "replace" });
      } else {
        window.location.reload();
      }
    } else if (data.action === "timeline_updated") {
      console.log(`Live update: Timeline ${data.timeline_type} menjadi ${data.status}`);

      if (window.Turbo) {
        Turbo.visit(window.location.href, { action: "replace" });
      } else {
        window.location.reload();
      }
    } else if (data.action === "refresh_video") {
      console.log("Live update: Refreshing video output frame...");
      const videoFrame = document.getElementById("video_output_frame");

      if (videoFrame) {
        videoFrame.src = window.location.href;
      }
    }
  }

  handleResize() {
    this.initTimelineSwiper();
  }

  initTimelineSwiper() {
    const swiperEl = this.element.querySelector('.tracking-timeline-swiper');
    const containerEl = this.element.querySelector('.tracking-timeline-container');
    if (!swiperEl || !containerEl) return;

    const isMobile = window.innerWidth < 1024;
    const slides = swiperEl.querySelectorAll('.swiper-slide');
    const prevBtnWrapper = this.element.querySelector('.timeline-prev-btn')?.parentElement;
    const nextBtnWrapper = this.element.querySelector('.timeline-next-btn')?.parentElement;

    const needsSwiper = isMobile && slides.length > 3;

    if (needsSwiper) {
      containerEl.classList.remove('swiper-disabled');
      if (prevBtnWrapper) prevBtnWrapper.classList.remove('hidden');
      if (nextBtnWrapper) nextBtnWrapper.classList.remove('hidden');

      if (this.timelineSwiper) return; // already initialized

      const activeIndex = parseInt(containerEl.dataset.activeIndex || 0);
      const activeSlideEl = swiperEl.querySelector(`[data-slide-index="${activeIndex}"]`);
      const activeStatus = activeSlideEl ? activeSlideEl.dataset.status : '';
      const shouldCenter = activeStatus === 'failed' || activeStatus === 'in_progress' || activeStatus === 'up_next';

      this.timelineSwiper = new Swiper(swiperEl, {
        direction: 'vertical',
        slidesPerView: "auto",
        centeredSlides: shouldCenter,
        centeredSlidesBounds: shouldCenter,
        initialSlide: activeIndex,
        allowTouchMove: true, // Enabled: allows users to swipe/drag with finger on mobile
        slideToClickedSlide: true, // Allows clicking a step to slide to it
        resistanceRatio: 0,
        speed: 350,
        on: {
          init: (swiper) => {
            this.updateNavigationButtons(swiper);
            this.updateMaskClasses(swiper);
            // Force recalculation and slide to the active index after the DOM settles
            setTimeout(() => {
              swiper.update();
              swiper.slideTo(activeIndex, 0, false);
              this.updateMaskClasses(swiper);
            }, 100);
          },
          slideChange: (swiper) => {
            this.updateNavigationButtons(swiper);
            this.updateMaskClasses(swiper);
          },
          setTranslate: (swiper, translate) => {
            this.updateMaskClasses(swiper);
          }
        }
      });
    } else {
      containerEl.classList.add('swiper-disabled');
      if (prevBtnWrapper) prevBtnWrapper.classList.add('hidden');
      if (nextBtnWrapper) nextBtnWrapper.classList.add('hidden');
      this.destroyTimelineSwiper();
    }
  }

  updateMaskClasses(swiper) {
    const swiperEl = swiper.el;
    if (!swiperEl) return;

    // Check pixel translations directly for 100% bulletproof boundary detection
    const currentTranslate = swiper.translate;
    const minTranslate = swiper.minTranslate();
    const maxTranslate = swiper.maxTranslate();
    const totalSlides = swiper.slides.length;

    // We are at the top if we are within 10px of minTranslate or if activeIndex is 0 or 1
    const isTop = swiper.isBeginning || swiper.activeIndex <= 1 || currentTranslate >= (minTranslate - 10);
    // We are at the bottom if we are within 10px of maxTranslate or if activeIndex is near the end
    const isBottom = swiper.isEnd || swiper.activeIndex >= (totalSlides - 2) || currentTranslate <= (maxTranslate + 10);

    if (isTop) {
      swiperEl.classList.add('swiper-at-top');
    } else {
      swiperEl.classList.remove('swiper-at-top');
    }

    if (isBottom) {
      swiperEl.classList.add('swiper-at-bottom');
    } else {
      swiperEl.classList.remove('swiper-at-bottom');
    }
  }

  updateSwiperHeight(swiper) {
    const swiperEl = this.element.querySelector('.tracking-timeline-swiper');
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!swiperEl || !activeSlide) return;

    const peek = 64; // px
    const hasPrev = swiper.activeIndex > 0;
    const hasNext = swiper.activeIndex < swiper.slides.length - 1;

    const topPeek    = hasPrev ? peek : 0;
    const bottomPeek = hasNext ? peek : 0;

    swiperEl.style.height = `${activeSlide.scrollHeight + topPeek + bottomPeek}px`;
  }

  destroyTimelineSwiper() {
    if (this.timelineSwiper) {
      this.timelineSwiper.destroy(true, true);
      this.timelineSwiper = null;
    }
  }

  prevSwiper(event) {
    if (event) event.preventDefault();
    if (this.timelineSwiper) {
      this.timelineSwiper.slidePrev();
    }
  }

  nextSwiper(event) {
    if (event) event.preventDefault();
    if (this.timelineSwiper) {
      this.timelineSwiper.slideNext();
    }
  }

  updateNavigationButtons(swiper) {
    const prevBtn = this.element.querySelector('.timeline-prev-btn');
    const nextBtn = this.element.querySelector('.timeline-next-btn');
    if (!prevBtn || !nextBtn) return;

    if (swiper.isBeginning) {
      prevBtn.classList.add('opacity-30', 'pointer-events-none');
    } else {
      prevBtn.classList.remove('opacity-30', 'pointer-events-none');
    }

    if (swiper.isEnd) {
      nextBtn.classList.add('opacity-30', 'pointer-events-none');
    } else {
      nextBtn.classList.remove('opacity-30', 'pointer-events-none');
    }
  }

  showLiveBanner() {
    if (this.hasLiveBannerTarget) {
      this.liveBannerTarget.classList.remove("hidden");
    }
    if (this.hasLivePulseTarget) {
      this.livePulseTarget.classList.remove("hidden");
    }
    if (this.hasLiveDotTarget) {
      this.liveDotTarget.classList.remove("bg-amber-500");
      this.liveDotTarget.classList.add("bg-emerald");
    }
    if (this.hasLiveStatusTitleTarget) {
      this.liveStatusTitleTarget.textContent = "Live Mission Active";
    }
    if (this.hasLiveStatusDescTarget) {
      this.liveStatusDescTarget.textContent = "Pilot is currently capturing your footage";
    }
  }

  showPausedState() {
    if (this.hasLivePulseTarget) {
      this.livePulseTarget.classList.add("hidden");
    }
    if (this.hasLiveDotTarget) {
      this.liveDotTarget.classList.remove("bg-emerald");
      this.liveDotTarget.classList.add("bg-amber-500");
    }
    if (this.hasLiveStatusTitleTarget) {
      this.liveStatusTitleTarget.textContent = "Flight Paused";
    }
    if (this.hasLiveStatusDescTarget) {
      this.liveStatusDescTarget.textContent = "The pilot has temporarily paused the flight";
    }
  }

  hideLiveBanner() {
    if (this.hasLiveBannerTarget) {
      this.liveBannerTarget.classList.add("hidden");
    }
  }

  updateTelemetry(data) {
    if (data.flight_time !== undefined && this.hasFlightTimeTarget) {
      const m = Math.floor(data.flight_time / 60);
      const s = data.flight_time % 60;
      this.flightTimeTarget.textContent = `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }
    if (data.altitude !== undefined && this.hasFlightAltTarget) {
      this.flightAltTarget.textContent = data.altitude;
    }
    if (data.distance !== undefined && this.hasFlightDistTarget) {
      this.flightDistTarget.textContent = data.distance;
    }
  }

}
